import { produce } from 'immer';
import set from 'lodash/set';
import { bool, func, objectOf, shape, string } from 'prop-types';
import qs from 'query-string';
import React from 'react';
import { withRouter } from 'react-router-dom';
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';

import {
  CHANGE_PRODUCT_BUDGET,
  TRANSFER_BUDGET,
} from '../../change-requests/constants';
import OPTIONS from '../../change-requests/options';
import SingleSelect from '../../generic/SingleSelect';
import { isSuperuser } from '../../layout/Variant/Variant';
import { campaignLineShape, campaignShape } from '../../prop-types';
import EmptyComponent from '../../utils/empty-component';
import { flash, tapFlashError } from '../../utils/flash';
import getEventTargetValue from '../../utils/get-event-target-value';
import { DISCRETIONARY_MEDIA } from '../constants';

const TypePending = () => <FormGroup>Please select a type</FormGroup>;

class CreateChangeRequestForm extends React.Component {
  state = {
    selectedType: null,
    comment: '',
    change: {},
    additionalParams: {},
    targetCampaign: undefined,
    targetProductAggregates: undefined,
    targetCommercialIncentive: undefined,
  };

  handleChange = event => {
    const { name } = event.target;
    const value = getEventTargetValue(event.target);
    const { campaign, campaignLinesById } = this.props;

    if (name === 'type') {
      const selectedType = OPTIONS.find(option => option.value === value);
      this.setState({
        selectedType,
        change: selectedType.initialState(campaign, campaignLinesById),
      });
    }

    if (name === 'comment') {
      this.setState({ comment: value });
    }
  };

  handleSpecificationChange = event => {
    const { name } = event.target;
    const value = getEventTargetValue(event.target);

    this.setState(({ change }) => ({
      change: produce(change, mutableChange => set(mutableChange, name, value)),
    }));
  };

  handleAdditionalParamsChange = event => {
    const { name } = event.target;
    const value = getEventTargetValue(event.target);

    this.setState(({ additionalParams }) => ({
      additionalParams: produce(additionalParams, mutableChange =>
        set(mutableChange, name, value),
      ),
    }));
  };

  setTargetProductAggregates = productAggregates => {
    this.setState({
      targetProductAggregates: { productAggregates },
    });
  };

  setTargetCampaign = campaign => {
    this.setState({
      targetCampaign: campaign,
    });
  };

  setTargetCommercialIncentive = event => {
    const { value } = event.target;
    this.setState({
      targetCommercialIncentive: value,
    });
  };

  handleTargetProductAggregatesChange = e => {
    const { name, value: newValue } = e.target;
    const productName = name.split('.')[1];
    const fieldName = name.split('.')[3];

    const productAggregateCopy = {
      ...this.state.targetProductAggregates.productAggregates,
    };
    const updatedProductAggregate = {
      ...productAggregateCopy[productName],
      newValues: {
        ...productAggregateCopy[productName].newValues,
        [fieldName]: newValue,
      },
    };

    productAggregateCopy[productName] = updatedProductAggregate;
    this.setState({
      targetProductAggregates: {
        productAggregates: { ...productAggregateCopy },
      },
    });
  };

  handleResetAdditionalParams = () => {
    this.setState({
      additionalParams: {},
    });
  };

  handleSubmit = event => {
    const {
      campaign,
      campaignLinesById,
      onRequest,
      toggle,
      loading,
      history,
      location,
    } = this.props;
    const {
      selectedType,
      change,
      comment,
      additionalParams,
      targetCampaign,
      targetProductAggregates,
      targetCommercialIncentive,
    } = this.state;

    const isTransferBudgetCR = selectedType?.value === TRANSFER_BUDGET;

    event.preventDefault(); // prevent navigating away
    event.stopPropagation();

    // Do not allow multiple parallel requests
    if (loading) {
      return;
    }

    const payload = selectedType.patch(campaign, change, campaignLinesById);

    // targetPayload will be sent only if CR type is Transfer Budget
    const targetPayload = () => {
      if (targetCampaign) {
        const { campaignLines } = targetCampaign;
        const targetCampaignLinesById = Object.fromEntries(
          campaignLines.map(line => [line.id, line]),
        );
        return selectedType.patch(
          targetCampaign,
          targetProductAggregates,
          targetCampaignLinesById,
          targetCommercialIncentive,
        );
      }
      return {};
    };

    // For CR Change Product Budget, BE needs the key additionalDetails
    // to be part of the payload. This value can be copied from comment.
    if (selectedType.value === CHANGE_PRODUCT_BUDGET) {
      payload.additionalDetails = comment;
    }

    const changeRequest = () => {
      return {
        type: selectedType.value,
        commentText: comment,
        ...additionalParams,
        payload,
        ...(isTransferBudgetCR && { targetPayload: targetPayload() }),
      };
    };

    // Validation for commercialIncentive field is added here since
    // the BE had some difficulties in doing the validation at their end.
    if (
      payload.commercialIncentive === DISCRETIONARY_MEDIA &&
      !payload.additionalDetails
    ) {
      tapFlashError()({
        message:
          'Please make sure to provide additional details for the incentive Discretionary Media',
      });
      return;
    }
    onRequest({
      id: campaign.id,
      data: changeRequest(),
    })
      .then(() => {
        if (change && change.delta && change.delta > 0) {
          flash(
            `Through this change request, the campaign will have additional ${change.delta} euros more to spend, because the management fee in the product that is receiving the budget is lower.`,
            'warning',
            180,
          );
        }

        if (change && change.delta && change.delta < 0) {
          flash(
            `Through this change request, the campaign will have ${Math.abs(
              change.delta,
            )} euros less to spend, because the management fee in the product where the budget is taken from is higher.`,
            'warning',
            180,
          );
        }
      })
      .then(() => {
        const search = qs.parse(location.search);
        history.push({
          pathname: location.pathname,
          search: qs.stringify({ ...search, comments: 'open' }),
        });
        toggle(false);
      })
      .catch(console.error); // eslint-disable-line no-console
  };

  render() {
    const { campaign, toggle, loading, campaignLinesById } = this.props;
    const {
      selectedType,
      change,
      comment,
      additionalParams,
      targetCampaign,
      targetProductAggregates,
      targetCommercialIncentive,
    } = this.state;

    const ChangeForm = selectedType
      ? selectedType.Form || EmptyComponent
      : TypePending;

    const isDisabled =
      selectedType?.value === TRANSFER_BUDGET && !targetProductAggregates;

    return (
      <Form onSubmit={this.handleSubmit}>
        <ModalHeader toggle={toggle}>Request Campaign Change</ModalHeader>
        <ModalBody>
          <FormGroup row>
            <Label for="createChangeRequestType" sm={2}>
              Type
            </Label>
            <Col sm={10}>
              <SingleSelect
                value={selectedType ? selectedType.value : ''}
                id="createChangeRequestType"
                name="type"
                options={OPTIONS.filter(
                  option =>
                    !option.inactive &&
                    (!option.superuserOnly || isSuperuser()),
                )}
                onChange={this.handleChange}
              />
            </Col>
          </FormGroup>
          <ChangeForm
            value={change}
            onChange={this.handleSpecificationChange}
            additionalParams={additionalParams}
            onAdditionalParamsChange={this.handleAdditionalParamsChange}
            onResetAdditionalParams={this.handleResetAdditionalParams}
            campaign={campaign}
            campaignLinesById={campaignLinesById}
            targetProductsAggregatesOnChange={
              this.handleTargetProductAggregatesChange
            }
            targetProductAggregates={targetProductAggregates}
            setTargetProductAggregates={this.setTargetProductAggregates}
            targetCampaign={targetCampaign}
            setTargetCampaign={this.setTargetCampaign}
            targetCommercialIncentive={targetCommercialIncentive}
            setTargetCommercialIncentive={this.setTargetCommercialIncentive}
          />
          {selectedType && (
            <FormGroup className="mt-4" row>
              <Label for="createChangeRequestComment" sm={2}>
                Additional Details
              </Label>
              <Col sm={10}>
                <Input
                  id="createChangeRequestComment"
                  name="comment"
                  onChange={this.handleChange}
                  value={comment}
                />
              </Col>
            </FormGroup>
          )}
        </ModalBody>
        <ModalFooter>
          <Button onClick={toggle} color="secondary">
            Close
          </Button>
          <Button
            type="submit"
            disabled={loading || isDisabled}
            color="primary"
          >
            Request
          </Button>
        </ModalFooter>
      </Form>
    );
  }
}

CreateChangeRequestForm.propTypes = {
  campaign: campaignShape.isRequired,
  campaignLinesById: objectOf(campaignLineShape).isRequired,
  toggle: func.isRequired,
  location: shape({ search: string }).isRequired,
  history: shape({ push: func.isRequired }).isRequired,
  // For submitting the request
  onRequest: func.isRequired,
  loading: bool.isRequired,
};

export default withRouter(CreateChangeRequestForm);
